import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowBack from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid } from 'react-data-grid';
import ReactHtmlParser from 'react-html-parser';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import { creators as StrategyActions } from '../../Reducers/Strategy';
import { strategyExportToPdf } from '../../Utils/Functions';
import ApiStrategy from '../../Api/ApiStrategy';
import StrategyExportButton from '../../Components/StrategyExportButton';
import ComboResponsibilityEditor from '../../Components/Editors/ComboResponsibilityEditor';
import DraftEditor from '../../Components/Editors/DraftEditor';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  selectStopPropagation,
  handleCopy,
} from '../../Components/ReactDataGridProps';
import ParentTable from './ParentTable';

const workTypes = [
  '0 Poor',
  '1 Poor',
  '2 Poor',
  '3 Poor',
  '4 Needs work',
  '5 Needs work',
  '6 Needs work',
  '7 Good',
  '8 Good',
  '9 Great',
  '10 Great',
];

function getComparator(sortColumn) {
  switch (sortColumn) {
    case 'id':
    case 'BaseLineRating':
    case 'CurrentRating':
      return (a, b) => {
        return a[sortColumn] - b[sortColumn];
      };
    case 'Description':
    case 'Responsibility':
    case 'MeasureTarget':
    case 'Comment':
      return (a, b) => {
        return a[sortColumn]?.localeCompare(b[sortColumn]);
      };
    default:
      throw new Error(`unsupported sortColumn: "${sortColumn}"`);
  }
}

function ObjectiveTable({
  selectedPlanId,
  planHeaders,
  onRouteBack,
  handleShowChildren,
  handleSelParentIdChange,
  companyLogo,
  byLine,
}) {
  const planSummaries = useSelector((state) =>
    state.Strategy.plansummariesPlanId === selectedPlanId
      ? state.Strategy.plansummaries
      : [],
  );
  const dispatch = useDispatch();

  const columnWidthRef = useRef(0);
  const columnIndexRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [parentShow, setParentShow] = useState(false);
  const [parentRows, setParentRows] = useState([]);
  const [parentTmpArr, setParentTmpArr] = useState([]);
  const [resizeCall, setResizeCall] = useState(true);
  const [columnWidth, setColumnWidth] = useState({});
  const [filters, setFilters] = useState({
    Description: '',
    Responsibility: '',
    BaseLineRating: 'All',
    CurrentRating: 'All',
    MeasureTarget: '',
    Comment: '',
    enabled: false,
  });
  const [sortColumns, setSortColumns] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(-1);
  const [editingRowId, setEditingRowId] = useState(null);

  const parentId = planHeaders?.find(
    (header) => header.id === selectedPlanId,
  )?.DimPlanHeaderKey_ParentID;

  useEffect(() => {
    setLoading(true);
    setRows([]);
    setParentRows([]);
    setParentTmpArr([]);
    setSelectedRowId(-1);
    dispatch(StrategyActions.strategyGetPlansummaries(selectedPlanId));
  }, [dispatch, selectedPlanId]);

  useEffect(() => {
    if (editingRowId === null) {
      setRows(planSummaries);
    }
    getParentData();
    // eslint-disable-next-line
  }, [planSummaries, editingRowId]);

  const getParentData = () => {
    const parentHeader = planHeaders?.find((header) => header.id === parentId);
    if (parentHeader) {
      if (parentHeader.PlanType === 'Objective') {
        ApiStrategy.getStrategyPlanSummaries(parentId)
          .then((res) => {
            if (res.status === 200) {
              setParentRows(res.data);
              if (res.data.length > 0 && parentId !== 0) {
                let parentTmps = [];
                res.data.forEach((row) => {
                  if (row.Description)
                    parentTmps.push({
                      id: row.id,
                      desc: row.Description,
                    });
                });
                setParentTmpArr(parentTmps);
              }
            }
          })
          .catch((error) => toast.error(error.message));
      } else {
        ApiStrategy.getStrategyPlanDetails(parentId)
          .then((res) => {
            if (res.status === 200) {
              setParentRows(res.data);
              if (res.data.length > 0 && parentId !== 0) {
                let parentTmps = [];
                res.data.forEach((row, index) => {
                  if (index === 0)
                    parentTmps.push({
                      id: row.id,
                      desc: row.Description,
                    });
                  else {
                    if (
                      res.data[index].Row_ID !== res.data[index - 1].Row_ID &&
                      row.Description
                    ) {
                      parentTmps.push({
                        id: row.id,
                        desc: row.Description,
                      });
                    }
                  }
                });
                setParentTmpArr(parentTmps);
              }
            } else toast.error(res.message);
          })
          .catch((error) => toast.error(error.message));
      }
    } else {
      setParentRows([]);
      setParentTmpArr([]);
    }

    ApiStrategy
      .getStrategyPlanSummaryWidth()
      .then((res) => {
        if (res.status === 200) {
          if (res.data) {
            setColumnWidth(JSON.parse(res.data));
          }
          setLoading(false);
        } else {
          toast.error(res.message);
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const handleColumnResize = (colIndex, width) => {
    columnWidthRef.current = width;
    columnIndexRef.current = colIndex.idx;

    if (resizeCall) {
      setResizeCall(false);
      setTimeout(() => {
        setResizeCall(true);
        handleStoreColumnResize();
      }, 3000);
    }
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = ['BaseLineRating', 'CurrentRating', 'SubPlan'];
    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey) ||
      targetColumnKey.includes('_tick') ||
      sourceColumnKey.includes('_tick') ||
      targetColumnKey.includes('_percent') ||
      sourceColumnKey.includes('_percent')
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const onClickedAdd = async () => {
    await ApiStrategy
      .addStrategyPlanSummary(selectedPlanId)
      .then((res) => {
        if (res.status === 200)
          dispatch(StrategyActions.strategyGetPlansummaries(selectedPlanId));
        else toast.warning(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const handleRowChange = async (updatedRows, field) => {
    setRows(updatedRows);
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };

    await ApiStrategy
      .updateStrategyPlanSummary(updated_data)
      .then((res) => {
        if (res.status === 200)
          dispatch(StrategyActions.strategyGetPlansummaries(selectedPlanId));
        else toast.warning(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const handleCellClick = (rowData) => {
    if (selectedRowId !== rowData.row.id) {
      setSelectedRowId(rowData.row.id);
    }
  };

  const onClickedDelete = async () => {
    if (selectedRowId < 0) return;
    await ApiStrategy
      .deleteStrategyPlanSummary(selectedRowId)
      .then((res) => {
        if (res.status === 200) {
          dispatch(StrategyActions.strategyGetPlansummaries(selectedPlanId));
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handleStoreColumnResize = async () => {
    let columnWidth = {};
    tableColumns.forEach((column) => {
      if (column.width) columnWidth[column.key] = column.width;
    });

    columnWidth[tableColumns[columnIndexRef.current].key] =
      columnWidthRef.current;

    await ApiStrategy
      .saveStrategyPlanSummaryWidth(columnWidth)
      .then((res) => {
        if (res.status === 200) {
          console.log('res', res);
        } else {
          toast.error(res.message);
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const tableColumns = useMemo(() => {
    let table_columns = [
      {
        key: 'id',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b>No</b>
          </div>
        ),
        width: 50,
        sortable: true,
        frozen: true,
        renderCell: (props) => {
          return (
            <div style={{ justifyContent: 'left', padding: '0 4px' }}>
              {props.rowIdx + 1}
            </div>
          );
        },
      },
      {
        key: 'Description',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>
              {
                planHeaders?.find((header) => header.id === selectedPlanId)
                  ?.Name
              }
            </b>
            {sortColumns.find((column) => column.columnKey === 'Description')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Description')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        width: columnWidth.Description ? 377 : 'auto',
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Description}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Description: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: (props) => {
          setEditingRowId(props.row.id);
          return (
            <DraftEditor
              keyValue="Description"
              value={props.row.Description}
              onCommit={(value) => {
                setEditingRowId(null);
                props.onRowChange({ ...props.row, Description: value }, true);
              }}
            />
          );
        },
        renderCell: (props) => {
          return (
            <div className="htmlParser">
              {ReactHtmlParser(props.row.Description)}
            </div>
          );
        },
      },
      {
        key: 'Responsibility',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Responsibility</b>
            {sortColumns.find((column) => column.columnKey === 'Responsibility')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'Responsibility',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        width: columnWidth.Responsibility ? columnWidth.Responsibility : 200,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Responsibility}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Responsibility: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: (props) => {
          setEditingRowId(props.row.id);
          return (
            <ComboResponsibilityEditor
              value={props.row.Responsibility}
              onCommit={(value) => {
                setEditingRowId(null);
                props.onRowChange({ ...props.row, Responsibility: value }, true);
              }}
            />
          );
        },
        renderCell: (props) => {
          let values = props.row.Responsibility
            ? props.row.Responsibility.split(',')
            : [];

          return (
            <div
              style={{ padding: '0 4px', height: '100%', lineHeight: '22px' }}
            >
              {values.join(', ')}
            </div>
          );
        },
      },
      {
        key: 'BaseLineRating',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>BaseLine</b>
            {sortColumns.find((column) => column.columnKey === 'BaseLineRating')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'BaseLineRating',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 138,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.BaseLineRating}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    BaseLineRating: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {workTypes.map((workType, index) => (
                  <option key={index} value={index}>
                    {workType}
                  </option>
                ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => (
          <Select
            autoFocus
            value={props.row.BaseLineRating ? props.row.BaseLineRating : ''}
            style={{
              height: '100%',
              width: 137,
              backgroundColor:
                props.row.BaseLineRating > 6
                  ? '#8dc63f'
                  : props.row.BaseLineRating > 3
                    ? '#f39243'
                    : '#de1e1e',
            }}
            onChange={(e) =>
              props.onRowChange(
                { ...props.row, BaseLineRating: e.target.value },
                true,
              )
            }
          >
            {workTypes.map((workType, index) => (
              <MenuItem
                key={index}
                style={{
                  backgroundColor:
                    index > 6 ? '#8dc63f' : index > 3 ? '#f39243' : '#de1e1e',
                }}
                value={index}
              >
                {workType}
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        key: 'MeasureTarget',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Measure | Target</b>
            {sortColumns.find((column) => column.columnKey === 'MeasureTarget')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'MeasureTarget',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        width: columnWidth.MeasureTarget ? columnWidth.MeasureTarget : 'auto',
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.MeasureTarget}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    MeasureTarget: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: (props) => {
          setEditingRowId(props.row.id);
          return (
            <DraftEditor
              keyValue="MeasureTarget"
              value={props.row.MeasureTarget}
              onCommit={(value) => {
                setEditingRowId(null);
                props.onRowChange({ ...props.row, MeasureTarget: value }, true);
              }}
            />
          );
        },
        renderCell: (props) => (
          <div className="htmlParser">
            {ReactHtmlParser(props.row.MeasureTarget)}
          </div>
        ),
      },
      {
        key: 'CurrentRating',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Current Status</b>
            {sortColumns.find((column) => column.columnKey === 'CurrentRating')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'CurrentRating',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 138,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.CurrentRating}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    CurrentRating: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {workTypes.map((workType, index) => (
                  <option key={index} value={index}>
                    {workType}
                  </option>
                ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => (
          <Select
            autoFocus
            value={props.row.CurrentRating ? props.row.CurrentRating : ''}
            style={{
              height: '100%',
              width: 137,
              backgroundColor:
                props.row.CurrentRating > 6
                  ? '#8dc63f'
                  : props.row.CurrentRating > 3
                    ? '#f39243'
                    : '#de1e1e',
            }}
            onChange={(e) =>
              props.onRowChange(
                { ...props.row, CurrentRating: e.target.value },
                true,
              )
            }
          >
            {workTypes.map((workType, index) => (
              <MenuItem
                key={index}
                style={{
                  backgroundColor:
                    index > 6 ? '#8dc63f' : index > 3 ? '#f39243' : '#de1e1e',
                }}
                value={index}
              >
                {workType}
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        key: 'Comment',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Comment</b>
            {sortColumns.find((column) => column.columnKey === 'Comment')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Comment')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        width: columnWidth.Comment ? columnWidth.Comment : 'auto',
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Comment}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Comment: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: (props) => {
          setEditingRowId(props.row.id);
          return (
            <DraftEditor
              keyValue="Comment"
              value={props.row.Comment}
              onCommit={(value) => {
                setEditingRowId(null);
                props.onRowChange({ ...props.row, Comment: value }, true);
              }}
            />
          );
        },
        renderCell: (props) => (
          <div className="htmlParser">{ReactHtmlParser(props.row.Comment)}</div>
        ),
      },
      {
        key: 'SubPlan',
        name: ' Sub Plan',
        width: 70,
        renderCell: (props) => {
          return (
            <div style={{ paddingLeft: 2 }}>
              <Button
                className="sub_plan"
                size="small"
                onClick={() => {
                  handleShowChildren(props.row.id);
                }}
              >
                View
              </Button>
            </div>
          );
        },
      },
    ];

    if (parentTmpArr.length > 0) {
      table_columns.splice(1, 0, {
        key: 'ParentKey',
        name: 'Parent Link',
        resizable: true,
        width: columnWidth.ParentKey ? columnWidth.ParentKey : 300,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.ParentKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    ParentKey: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: (props) => (
          <Select
            autoFocus
            value={props.row.ParentKey}
            className="rdg-select-editor"
            onChange={(e) =>
              props.onRowChange(
                { ...props.row, ParentKey: e.target.value },
                true,
              )
            }
          >
            {parentTmpArr.map((row, index) => (
              <MenuItem
                key={index}
                value={row.id}
                className="htmlParser"
                style={{
                  fontSize: 13,
                  width: columnWidth.ParentKey ? columnWidth.ParentKey : 300,
                }}
              >
                {ReactHtmlParser(row.desc)}
              </MenuItem>
            ))}
          </Select>
        ),
        renderCell: (props) => (
          <Tooltip
            title={
              props.row.ParentKey
                ? ReactHtmlParser(
                    parentTmpArr.find((row) => row.id === props.row.ParentKey)
                      ?.desc,
                  )
                : ''
            }
            placement="right-start"
          >
            <div className="htmlParser">
              {props.row.ParentKey
                ? ReactHtmlParser(
                    parentTmpArr.find((row) => row.id === props.row.ParentKey)
                      ?.desc,
                  )
                : ''}
            </div>
          </Tooltip>
        ),
      });
    }

    return table_columns;
    // eslint-disable-next-line
  }, [sortColumns, parentTmpArr, columnWidth]);

  const filteredRows = useMemo(() => {
    return rows?.filter((r) => {
      return (
        (filters.BaseLineRating !== 'All'
          ? // eslint-disable-next-line
            r.BaseLineRating == filters.BaseLineRating
          : true) &&
        (filters.CurrentRating !== 'All'
          ? // eslint-disable-next-line
            r.CurrentRating == filters.CurrentRating
          : true) &&
        (filters.Description
          ? r.Description?.toLowerCase().includes(
              filters.Description.toLowerCase(),
            )
          : true) &&
        (filters.Responsibility
          ? r.Responsibility?.toLowerCase().includes(
              filters.Responsibility.toLowerCase(),
            )
          : true) &&
        (filters.MeasureTarget
          ? r.MeasureTarget?.toLowerCase().includes(
              filters.MeasureTarget.toLowerCase(),
            )
          : true) &&
        (filters.Comment
          ? r.Comment?.toLowerCase().includes(filters.Comment.toLowerCase())
          : true)
      );
    });
  }, [rows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  const maxHeight = useMemo(() => {
    let max_height = window.innerHeight - 144;
    if (tableColumns.length > 0) {
      if (tableColumns[1].key === 'ParentKey') {
        max_height -= 40;

        if (parentShow) {
          max_height -= window.innerHeight / 3 - 110;
        }
      }
    }
    if (companyLogo) max_height -= 20;
    if (byLine) max_height -= 57;

    return max_height;
  }, [tableColumns, parentShow, companyLogo, byLine]);
  
  return (
    <>
      {tableColumns.length > 0 && tableColumns[1].key === 'ParentKey' ? (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <IconButton aria-label="Back" onClick={onRouteBack}>
            <ArrowBack />
          </IconButton>
          <FormControlLabel
            control={
              <Switch
                checked={parentShow}
                onChange={(ev) => {
                  setParentShow(ev.target.checked);
                  getParentData();
                }}
                disabled={parentId >= 0 ? false : true}
              />
            }
            label="Show/Hide Parent Plan"
          />
        </div>
      ) : null}
      {parentShow === true && parentId > 0 && (
        <Grid
          container
          space={6}
          style={{ height: window.innerHeight / 3 - 110 }}
        >
          <Grid size={12} style={{ height: '100%' }}>
            {parentId >= 0 && (
              <ParentTable
                selectedPlanId={parentId}
                planHeaders={planHeaders}
                parentRows={parentShow ? parentRows : []}
                onChangeSelId={handleSelParentIdChange}
              />
            )}
          </Grid>
        </Grid>
      )}

      <Box display="flex" alignItems="flex-end" justifyContent="space-between" my={0.5}>
        <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5} ml={1}>
          {companyLogo ? (
            <img
              src={`data:image/png;base64,${companyLogo}`}
              alt="company_logo"
              style={{
                height: 60,
                width: 'fit-content',
              }}
            />
          ) : null}
          {byLine ? (
            <Box display="flex">
              <p
                style={{
                  float: 'left',
                  padding: '10px 5px',
                  margin: 0,
                  background: 'rgb(224, 224, 224)',
                  fontSize: 17,
                }}
              >
                Our Why
              </p>
              <div className="by_line" style={{ fontSize: 17 }}>
                {ReactHtmlParser(byLine)}
              </div>
            </Box>
          ) : null}
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <StrategyExportButton
            tableHeight={maxHeight}
            onExport={() =>
              strategyExportToPdf(
                `${
                  planHeaders?.find((header) => header.id === selectedPlanId)
                    ?.Name
                }.pdf`,
              )
            }
          >
            Export to PDF
          </StrategyExportButton>
          <IconButton aria-label="Add" onClick={onClickedAdd}>
            <AddIcon />
          </IconButton>
          <IconButton aria-label="Search" onClick={toggleFilters}>
            <SearchIcon />
          </IconButton>
          <IconButton aria-label="Delete" onClick={onClickedDelete}>
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>

      {loading === false && (
        <FilterContext.Provider value={filters}>
          <DataGrid
            rowKeyGetter={rowKeyGetter}
            className={
              filters.enabled
                ? 'filter-container strategy_table fill-grid rdg-light'
                : 'strategy_table fill-grid rdg-light'
            }
            columns={tableColumns}
            rows={sortedRows}
            headerRowHeight={filters.enabled ? 70 : 50}
            rowClass={(row, index) =>
              index % 2 === 1 ? 'rdg-highlight-cell' : undefined
            }
            sortColumns={sortColumns}
            onSortColumnsChange={setSortColumns}
            onRowsChange={handleRowChange}
            onCellClick={handleCellClick}
            onColumnResize={handleColumnResize}
            onCopy={handleCopy}
            onPaste={handlePaste}
            rowHeight={(row) => rowHeight(row, parentTmpArr, tableColumns)}
            style={{ height: maxHeight, resize: 'both' }}
          />
        </FilterContext.Provider>
      )}
    </>
  );
}

ObjectiveTable.propTypes = {
  selectedPlanId: PropTypes.number.isRequired,
  selParentId: PropTypes.number.isRequired,
  planHeaders: PropTypes.array.isRequired,
  onRouteBack: PropTypes.func.isRequired,
  handleShowChildren: PropTypes.func.isRequired,
  handleSelParentIdChange: PropTypes.func.isRequired,
  companyLogo: PropTypes.string.isRequired,
  byLine: PropTypes.string.isRequired,
};

export default ObjectiveTable;

function getColumnWidth(tableColumns, rowKey, fallbackWidth = 250) {
  const column = tableColumns.find((item) => item.key === rowKey);
  const width = parseInt(column?.width, 10);
  return Number.isNaN(width) ? fallbackWidth : width;
}

function getLineAwareText(value) {
  if (value === null || value === undefined) return '';

  const rawValue = value.toString();
  const lineAwareHtml = rawValue
    .replace(/<br\s*\/?\s*>/gi, '<br />\n')
    .replace(/<\/(p|div|h[1-6]|li|tr)\s*>/gi, '</$1>\n')
    .replace(/<li\b[^>]*>/gi, '• ');

  if (/<\/?[a-z][\s\S]*>/i.test(lineAwareHtml)) {
    if (typeof DOMParser !== 'undefined') {
      const parser = new DOMParser();
      const doc = parser.parseFromString(lineAwareHtml, 'text/html');
      return doc.body.textContent || '';
    }

    return lineAwareHtml.replace(/<[^>]*>/g, '');
  }

  return lineAwareHtml;
}

function getEstimatedLineCount(value, columnWidth = 250) {
  const text = getLineAwareText(value)
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n|\r/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .trim();

  if (!text) return 1;

  const availableWidth = Math.max(columnWidth - 12, 48);
  const charsPerLine = Math.max(Math.floor(availableWidth / 6), 8);

  return text
    .split('\n')
    .filter((line) => line.trim().length > 0)
    .reduce((count, line) => count + Math.max(Math.ceil(line.length / charsPerLine), 1), 0);
}

function rowHeight(row, parentTmpArr, tableColumns) {
  const minHeight = 60;
  const lineHeight = 17;
  const verticalPadding = 12;

  const rowCellMaxLineCount = Object.keys(row).reduce((maxLines, rowKey) => {
    const lines = getEstimatedLineCount(
      row[rowKey],
      getColumnWidth(tableColumns, rowKey),
    );
    return Math.max(maxLines, lines);
  }, 1);

  const parentDescription = row.ParentKey && parentTmpArr.length > 0
    ? parentTmpArr.find((temp) => temp.id === row.ParentKey)?.desc || ''
    : '';

  const parentLineCount = parentDescription
    ? getEstimatedLineCount(parentDescription, getColumnWidth(tableColumns, 'ParentKey', 300))
    : 1;

  return Math.max(
    minHeight,
    Math.max(rowCellMaxLineCount, parentLineCount) * lineHeight + verticalPadding,
  );
}
